import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { api } from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import {
  FileText,
  Calendar,
  IndianRupee,
  TrendingUp,
  AlertTriangle,
  Download,
  Loader2,
  CreditCard,
  Wallet,
  Users,
  Phone,
  ChevronRight,
  Clock,
  RefreshCw
} from 'lucide-react';
import { formatCurrency } from '@/utils/helpers';

function Reports() {
  const navigate = useNavigate();

  // Monthly Collection Report State
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [collectionReport, setCollectionReport] = useState(null);
  const [loadingCollection, setLoadingCollection] = useState(false);

  // Overdue Loans Report State
  const [overdueReport, setOverdueReport] = useState(null);
  const [loadingOverdue, setLoadingOverdue] = useState(false);

  // Active tab
  const [activeTab, setActiveTab] = useState('collection');

  // Fetch Monthly Collection Report
  const fetchCollectionReport = async () => {
    setLoadingCollection(true);
    try {
      const data = await api.reports.getMonthlyCollection(selectedMonth);
      setCollectionReport(data);
    } catch (error) {
      console.error('Failed to fetch collection report:', error);
      toast.error('Failed to load collection report');
    } finally {
      setLoadingCollection(false);
    }
  };

  // Fetch Overdue Loans Report
  const fetchOverdueReport = async () => {
    setLoadingOverdue(true);
    try {
      const data = await api.reports.getOverdueLoans();
      setOverdueReport(data);
    } catch (error) {
      console.error('Failed to fetch overdue report:', error);
      toast.error('Failed to load overdue report');
    } finally {
      setLoadingOverdue(false);
    }
  };

  // Load initial data
  useEffect(() => {
    fetchCollectionReport();
    fetchOverdueReport();
  }, []);

  // Reload collection report when month changes
  useEffect(() => {
    if (selectedMonth) {
      fetchCollectionReport();
    }
  }, [selectedMonth]);

  // Format month for display
  const formatMonth = (monthStr) => {
    const [year, month] = monthStr.split('-');
    const date = new Date(year, month - 1);
    return date.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // Export to CSV with transaction details
  const exportCollectionCSV = () => {
    if (!collectionReport) return;

    let csv = 'Monthly Collection Report\n';
    csv += `Month,${formatMonth(collectionReport.month)}\n\n`;
    csv += 'Summary\n';
    csv += `Total Collected,${collectionReport.total_collected}\n`;
    csv += `EMI Collected,${collectionReport.total_emi_collected}\n`;
    csv += `Lavajam Collected,${collectionReport.total_lavajam_collected}\n`;
    csv += `Cash Collected,${collectionReport.cash_collected}\n`;
    csv += `Total Transactions,${collectionReport.total_transactions}\n\n`;
    csv += 'Transaction Details\n';
    csv += 'Date,Member Name,Mobile,Type,Amount\n';
    collectionReport.daily_breakdown.forEach(day => {
      if (day.transactions && day.transactions.length > 0) {
        day.transactions.forEach(tx => {
          csv += `${day.date},"${tx.member_name}",${tx.mobile || 'N/A'},${tx.type},${tx.amount}\n`;
        });
      }
    });

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `collection-report-${collectionReport.month}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Report exported successfully');
  };

  const exportOverdueCSV = () => {
    if (!overdueReport) return;

    let csv = 'Overdue Loans Report\n';
    csv += `Report Date,${formatDate(overdueReport.report_date)}\n`;
    csv += `Total Overdue Members,${overdueReport.total_overdue_members}\n`;
    csv += `Total Overdue Amount,${overdueReport.total_overdue_amount}\n\n`;
    csv += 'Member Name,Account Number,Contact,Loan Account,EMI Amount,Overdue EMIs,Total Overdue,Oldest Due Date,Days Overdue\n';
    overdueReport.overdue_members.forEach(m => {
      csv += `"${m.member_name}",${m.account_number},${m.contact_number || 'N/A'},${m.loan_account_number},${m.emi_amount},${m.overdue_emis},${m.total_overdue_amount},${m.oldest_overdue_date},${m.days_overdue}\n`;
    });

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `overdue-report-${overdueReport.report_date}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Report exported successfully');
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 fade-in pt-2">
        {/* Header - Consistent spacing */}
        <div className="flex justify-between items-start pb-2">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold gradient-text mb-1">
              Reports & Analytics
            </h1>
            <p className="text-sm text-gray-600">View detailed reports and export data</p>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-2 border-b border-gray-200 pb-2">
          <Button
            variant={activeTab === 'collection' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('collection')}
            className={activeTab === 'collection' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
          >
            <IndianRupee className="w-4 h-4 mr-2" />
            Monthly Collection
          </Button>
          <Button
            variant={activeTab === 'overdue' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('overdue')}
            className={activeTab === 'overdue' ? 'bg-red-600 hover:bg-red-700' : ''}
          >
            <AlertTriangle className="w-4 h-4 mr-2" />
            Overdue Loans
            {overdueReport?.total_overdue_members > 0 && (
              <Badge variant="destructive" className="ml-2">
                {overdueReport.total_overdue_members}
              </Badge>
            )}
          </Button>
        </div>

        {/* Monthly Collection Report Tab */}
        {activeTab === 'collection' && (
          <div className="space-y-6">
            {/* Controls */}
            <Card className="glass">
              <CardContent className="pt-6">
                <div className="flex flex-wrap items-end gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="month">Select Month</Label>
                    <Input
                      id="month"
                      type="month"
                      value={selectedMonth}
                      onChange={(e) => setSelectedMonth(e.target.value)}
                      className="w-48"
                    />
                  </div>
                  <Button
                    onClick={fetchCollectionReport}
                    disabled={loadingCollection}
                    variant="outline"
                  >
                    {loadingCollection ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4 mr-2" />
                    )}
                    Refresh
                  </Button>
                  <Button
                    onClick={exportCollectionCSV}
                    disabled={!collectionReport || loadingCollection}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export CSV
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Collection Summary */}
            {loadingCollection ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
              </div>
            ) : collectionReport ? (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <Card className="glass border-l-4 border-l-emerald-500">
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-500">Total Collected</p>
                          <p className="text-lg md:text-2xl font-bold text-emerald-600">
                            {formatCurrency(collectionReport.total_collected)}
                          </p>
                        </div>
                        <div className="p-3 bg-emerald-100 rounded-full">
                          <IndianRupee className="w-6 h-6 text-emerald-600" />
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        {collectionReport.total_transactions} transactions
                      </p>
                    </CardContent>
                  </Card>

                  <Card className="glass border-l-4 border-l-blue-500">
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-500">EMI Collected</p>
                          <p className="text-lg md:text-2xl font-bold text-blue-600">
                            {formatCurrency(collectionReport.total_emi_collected)}
                          </p>
                        </div>
                        <div className="p-3 bg-blue-100 rounded-full">
                          <CreditCard className="w-6 h-6 text-blue-600" />
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        {collectionReport.emi_count} EMI payments
                      </p>
                    </CardContent>
                  </Card>

                  <Card className="glass border-l-4 border-l-purple-500">
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-500">Lavajam Collected</p>
                          <p className="text-lg md:text-2xl font-bold text-purple-600">
                            {formatCurrency(collectionReport.total_lavajam_collected)}
                          </p>
                        </div>
                        <div className="p-3 bg-purple-100 rounded-full">
                          <Wallet className="w-6 h-6 text-purple-600" />
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        {collectionReport.lavajam_count} Lavajam deposits
                      </p>
                    </CardContent>
                  </Card>

                  <Card className="glass border-l-4 border-l-orange-500">
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-500">Payment Mode</p>
                          <div className="flex gap-2 mt-1">
                            <Badge variant="outline" className="text-green-700 border-green-300 bg-green-50">
                              Cash: {formatCurrency(collectionReport.cash_collected)}
                            </Badge>
                          </div>
                          <div className="mt-1">
                            <Badge variant="outline" className="text-blue-700 border-blue-300 bg-blue-50">
                              UPI: {formatCurrency(collectionReport.upi_collected)}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Daily Breakdown */}
                <Card className="glass">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Calendar className="w-5 h-5" />
                      Daily Collection Breakdown
                    </CardTitle>
                    <CardDescription>
                      {formatMonth(collectionReport.month)}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {collectionReport.daily_breakdown.length > 0 ? (
                      <>
                        {/* Desktop Table View - Transaction Details */}
                        <div className="hidden md:block overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b bg-gray-50">
                                <th className="text-left py-3 px-4 font-semibold">Date</th>
                                <th className="text-left py-3 px-4 font-semibold">Member Name</th>
                                <th className="text-left py-3 px-4 font-semibold">Mobile</th>
                                <th className="text-center py-3 px-4 font-semibold">Type</th>
                                <th className="text-right py-3 px-4 font-semibold">Amount</th>
                              </tr>
                            </thead>
                            <tbody>
                              {collectionReport.daily_breakdown.flatMap((day, dayIdx) =>
                                (day.transactions || []).map((tx, txIdx) => (
                                  <tr key={`${day.date}-${txIdx}`} className={dayIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                    <td className="py-3 px-4 text-gray-700">{txIdx === 0 ? formatDate(day.date) : ''}</td>
                                    <td className="py-3 px-4 font-medium text-gray-900">{tx.member_name}</td>
                                    <td className="py-3 px-4 text-gray-600">{tx.mobile || 'N/A'}</td>
                                    <td className="text-center py-3 px-4">
                                      <Badge className={tx.type === 'EMI' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}>
                                        {tx.type}
                                      </Badge>
                                    </td>
                                    <td className="text-right py-3 px-4 font-semibold text-emerald-600">
                                      {formatCurrency(tx.amount)}
                                    </td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>

                        {/* Mobile Card View - Grouped by Date */}
                        <div className="md:hidden space-y-4">
                          {collectionReport.daily_breakdown.map((day, idx) => (
                            <div key={day.date} className="rounded-lg border bg-white shadow-sm">
                              {/* Date Header */}
                              <div className="flex items-center justify-between p-3 bg-gray-100 rounded-t-lg border-b">
                                <div className="font-semibold text-gray-900">{formatDate(day.date)}</div>
                                <div className="text-sm text-gray-600">
                                  {day.transaction_count || day.transactions?.length || 0} txns • {formatCurrency(day.total)}
                                </div>
                              </div>

                              {/* Transactions List */}
                              <div className="divide-y">
                                {(day.transactions || []).map((tx, txIdx) => (
                                  <div key={txIdx} className="p-3">
                                    <div className="flex items-center justify-between mb-1">
                                      <span className="font-medium text-gray-900">{tx.member_name}</span>
                                      <span className="text-lg font-bold text-emerald-600">{formatCurrency(tx.amount)}</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-sm text-gray-500">
                                      <span>{tx.mobile || 'No mobile'}</span>
                                      <span>•</span>
                                      <Badge className={tx.type === 'EMI' ? 'bg-blue-100 text-blue-700 text-xs' : 'bg-purple-100 text-purple-700 text-xs'}>
                                        {tx.type}
                                      </Badge>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-12 text-gray-500">
                        <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p>No collections recorded for {formatMonth(collectionReport.month)}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            ) : null}
          </div>
        )}

        {/* Overdue Loans Report Tab */}
        {activeTab === 'overdue' && (
          <div className="space-y-6">
            {/* Controls */}
            <Card className="glass">
              <CardContent className="pt-6">
                <div className="flex flex-wrap items-center gap-4">
                  <Button
                    onClick={fetchOverdueReport}
                    disabled={loadingOverdue}
                    variant="outline"
                  >
                    {loadingOverdue ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4 mr-2" />
                    )}
                    Refresh
                  </Button>
                  <Button
                    onClick={exportOverdueCSV}
                    disabled={!overdueReport || loadingOverdue}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export CSV
                  </Button>
                  {overdueReport && (
                    <span className="text-sm text-gray-500">
                      Report as of: {formatDate(overdueReport.report_date)}
                    </span>
                  )}
                </div>
              </CardContent>
            </Card>

            {loadingOverdue ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-red-600" />
              </div>
            ) : overdueReport ? (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card className="glass border-l-4 border-l-red-500">
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-500">Overdue Members</p>
                          <p className="text-xl md:text-3xl font-bold text-red-600">
                            {overdueReport.total_overdue_members}
                          </p>
                        </div>
                        <div className="p-3 bg-red-100 rounded-full">
                          <Users className="w-6 h-6 text-red-600" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="glass border-l-4 border-l-orange-500">
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-500">Total Overdue Amount</p>
                          <p className="text-xl md:text-3xl font-bold text-orange-600">
                            {formatCurrency(overdueReport.total_overdue_amount)}
                          </p>
                        </div>
                        <div className="p-3 bg-orange-100 rounded-full">
                          <IndianRupee className="w-6 h-6 text-orange-600" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="glass border-l-4 border-l-yellow-500">
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-500">Avg Overdue per Member</p>
                          <p className="text-xl md:text-3xl font-bold text-yellow-600">
                            {overdueReport.total_overdue_members > 0
                              ? formatCurrency(overdueReport.total_overdue_amount / overdueReport.total_overdue_members)
                              : '₹0'}
                          </p>
                        </div>
                        <div className="p-3 bg-yellow-100 rounded-full">
                          <TrendingUp className="w-6 h-6 text-yellow-600" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Overdue Members List */}
                <Card className="glass">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-red-700">
                      <AlertTriangle className="w-5 h-5" />
                      Overdue Members List
                    </CardTitle>
                    <CardDescription>
                      Members with pending EMI payments past due date
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {overdueReport.overdue_members.length > 0 ? (
                      <div className="space-y-3">
                        {overdueReport.overdue_members.map((member, idx) => (
                          <div
                            key={member.member_id}
                            className={`p-4 rounded-lg border ${member.days_overdue > 30
                              ? 'border-red-300 bg-red-50'
                              : member.days_overdue > 14
                                ? 'border-orange-300 bg-orange-50'
                                : 'border-yellow-300 bg-yellow-50'
                              }`}
                          >
                            <div className="flex flex-wrap items-start justify-between gap-4">
                              <div className="flex-1 min-w-[200px]">
                                <div className="flex items-center gap-2">
                                  <h4 className="font-semibold text-gray-900">
                                    {member.member_name}
                                  </h4>
                                  <Badge
                                    variant={
                                      member.days_overdue > 30
                                        ? 'destructive'
                                        : member.days_overdue > 14
                                          ? 'warning'
                                          : 'secondary'
                                    }
                                  >
                                    {member.days_overdue} days overdue
                                  </Badge>
                                </div>
                                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-gray-600">
                                  <span>ID: {member.account_number}</span>
                                  <span>Loan: {member.loan_account_number}</span>
                                  {member.contact_number && (
                                    <span className="flex items-center gap-1">
                                      <Phone className="w-3 h-3" />
                                      {member.contact_number}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="text-sm text-gray-500">Total Overdue</p>
                                <p className="text-xl font-bold text-red-600">
                                  {formatCurrency(member.total_overdue_amount)}
                                </p>
                                <p className="text-xs text-gray-500 flex items-center justify-end gap-1">
                                  <Clock className="w-3 h-3" />
                                  {member.overdue_emis} EMI(s) × {formatCurrency(member.emi_amount)}
                                </p>
                              </div>
                            </div>
                            <Separator className="my-3" />
                            <div className="flex justify-between items-center text-sm">
                              <span className="text-gray-500">
                                Oldest due: {formatDate(member.oldest_overdue_date)}
                              </span>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-blue-600"
                                onClick={() => navigate(`/manager/members/${member.member_id}`)}
                              >
                                View Member <ChevronRight className="w-4 h-4 ml-1" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-12 text-gray-500">
                        <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
                          <TrendingUp className="w-8 h-8 text-green-600" />
                        </div>
                        <p className="text-lg font-medium text-green-700">All Clear! 🎉</p>
                        <p className="text-sm">No overdue payments at this time</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            ) : null}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

export default Reports;
